import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import {
  Package,
  RefreshCw,
  Search,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ShoppingCart,
  Truck,
  Plus,
  Trash2,
  Play,
  Eye,
  Link,
  Calendar,
  Download,
  CheckSquare,
} from 'lucide-react';

// Types
interface Shop {
  id: string;
  shopName: string;
  platformType: string;
  isActive: boolean;
  lastSyncAt?: string;
}

interface OrderItem {
  id: string;
  mallItemCode: string;
  productName: string;
  quantity: number;
  unitPrice: string;
}

interface RakutenOrder {
  id: string;
  mallOrderId: string;
  orderStatus: string;
  customerInfo: {
    name: string;
    email?: string;
    phone?: string;
  };
  shippingAddress?: {
    name: string;
    postalCode: string;
    address: string;
    phone?: string;
  };
  totalAmount: string;
  orderedAt: string;
  shop?: Shop;
  items: OrderItem[];
  dropshipStatus?: string | null;
  amazonOrderId?: string | null;
}

interface ProductMapping {
  id: string;
  rakutenItemCode: string;
  rakutenItemName?: string;
  amazonAsin: string;
  amazonSku?: string;
  amazonItemName?: string;
  janCode?: string;
  isActive: boolean;
  rakutenShop?: Shop;
  amazonShop?: Shop;
}

interface DropshipOrder {
  id: string;
  rakutenMallOrderId: string;
  status: string;
  customerName: string;
  shippingName: string;
  shippingPostalCode: string;
  shippingAddress: string;
  amazonOrderId?: string;
  amazonOrderTotal?: string;
  amazonTrackingNumber?: string;
  createdAt: string;
  items: DropshipOrderItem[];
}

interface DropshipOrderItem {
  id: string;
  rakutenItemCode: string;
  rakutenItemName: string;
  amazonAsin?: string;
  quantity: number;
  amazonStock?: number;
  stockStatus?: string;
}

type TabType = 'orders' | 'dropship' | 'mappings';

const STATUS_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: '処理待ち', color: 'bg-gray-100 text-gray-700', icon: <Clock size={14} /> },
  checking_stock: { label: '在庫確認中', color: 'bg-blue-100 text-blue-700', icon: <RefreshCw size={14} className="animate-spin" /> },
  stock_available: { label: '在庫あり', color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} /> },
  stock_unavailable: { label: '在庫なし', color: 'bg-red-100 text-red-700', icon: <XCircle size={14} /> },
  ordering: { label: '発注処理中', color: 'bg-yellow-100 text-yellow-700', icon: <RefreshCw size={14} className="animate-spin" /> },
  ordered: { label: '発注完了', color: 'bg-indigo-100 text-indigo-700', icon: <ShoppingCart size={14} /> },
  shipping: { label: '配送中', color: 'bg-purple-100 text-purple-700', icon: <Truck size={14} /> },
  shipped: { label: '発送完了', color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} /> },
  cancelled: { label: 'キャンセル', color: 'bg-gray-100 text-gray-500', icon: <XCircle size={14} /> },
  failed: { label: '失敗', color: 'bg-red-100 text-red-700', icon: <AlertCircle size={14} /> },
};

export default function DropshipPage() {
  const [activeTab, setActiveTab] = useState<TabType>('orders');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 楽天受注
  const [rakutenOrders, setRakutenOrders] = useState<RakutenOrder[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [rakutenShops, setRakutenShops] = useState<Shop[]>([]);
  const [amazonShops, setAmazonShops] = useState<Shop[]>([]);
  
  // ページネーション
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [dropshipCurrentPage, setDropshipCurrentPage] = useState(1);
  const [selectedShopId, setSelectedShopId] = useState<string>('');
  const [syncing, setSyncing] = useState(false);

  // ドロップシッピング注文
  const [dropshipOrders, setDropshipOrders] = useState<DropshipOrder[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});

  // 商品マッピング
  const [mappings, setMappings] = useState<ProductMapping[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [newMapping, setNewMapping] = useState({
    rakutenShopId: '',
    amazonShopId: '',
    rakutenItemCode: '',
    rakutenItemName: '',
    amazonAsin: '',
    amazonSku: '',
    janCode: '',
  });

  // 詳細モーダル
  const [selectedDropshipOrder, setSelectedDropshipOrder] = useState<DropshipOrder | null>(null);

  // 期間指定
  const [syncDays, setSyncDays] = useState<number>(30);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  
  // 一括選択
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  // 店舗一覧取得
  const fetchShops = useCallback(async () => {
    try {
      const res = await api.get('/api/dropship/shops');
      const data = await res.json();
      setRakutenShops(data.rakutenShops || []);
      setAmazonShops(data.amazonShops || []);
      if (data.rakutenShops?.length > 0 && !selectedShopId) {
        setSelectedShopId(data.rakutenShops[0].id);
      }
    } catch (err) {
      console.error('Fetch shops error:', err);
    }
  }, [selectedShopId]);

  // 楽天受注一覧取得
  const fetchRakutenOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedShopId) params.set('shopId', selectedShopId);
      const res = await api.get(`/api/dropship/rakuten-orders?${params}`);
      const data = await res.json();
      setRakutenOrders(data.orders || []);
    } catch (err) {
      setError('楽天受注の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selectedShopId]);

  // 楽天API接続テスト
  const testRakutenConnection = async () => {
    if (!selectedShopId) {
      setError('楽天ショップを選択してください');
      return;
    }
    setSyncing(true);
    setError(null);
    try {
      const res = await api.post('/api/dropship/test-rakuten-connection', {
        shopId: selectedShopId,
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ 接続成功！\n\nショップ: ${data.shopName}\n取得件数: ${data.ordersCount}件\n\nAPIキー: ${data.apiKeyPreview}\nライセンスキー: ${data.apiSecretPreview}`);
      } else {
        setError(`接続テスト失敗: ${data.error}\n詳細: ${JSON.stringify(data.details || {})}`);
      }
    } catch (err) {
      setError('接続テストに失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  // 楽天受注同期
  const syncRakutenOrders = async () => {
    if (!selectedShopId) {
      setError('楽天ショップを選択してください');
      return;
    }
    setSyncing(true);
    setError(null);
    try {
      const res = await api.post('/api/dropship/fetch-rakuten-orders', {
        shopId: selectedShopId,
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        await fetchRakutenOrders();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('楽天受注の同期に失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  // ドロップシッピング注文作成
  const createDropshipOrder = async (rakutenOrderId: string) => {
    try {
      const res = await api.post('/api/dropship/orders/create', { rakutenOrderId });
      const data = await res.json();
      if (data.success) {
        alert('ドロップシッピング注文を作成しました');
        await fetchRakutenOrders();
        await fetchDropshipOrders();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('ドロップシッピング注文の作成に失敗しました');
    }
  };

  // ドロップシッピング注文一覧取得
  const fetchDropshipOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/dropship/orders');
      const data = await res.json();
      setDropshipOrders(data.orders || []);
    } catch (err) {
      setError('ドロップシッピング注文の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 統計取得
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/dropship/stats');
      const data = await res.json();
      setStats(data.stats || {});
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  }, []);

  // Amazon在庫確認
  const checkAmazonStock = async (dropshipOrderId: string) => {
    try {
      const res = await api.post('/api/dropship/check-amazon-stock', { dropshipOrderId });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        await fetchDropshipOrders();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Amazon在庫確認に失敗しました');
    }
  };

  // Amazon発注実行
  const executeOrder = async (dropshipOrderId: string) => {
    if (!confirm('Amazonで購入処理を実行しますか？')) return;
    try {
      const res = await api.post('/api/dropship/execute-order', { dropshipOrderId });
      const data = await res.json();
      if (data.success) {
        alert(`発注完了: ${data.amazonOrderId}\n合計: ¥${data.totalPrice.toLocaleString()}`);
        await fetchDropshipOrders();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Amazon発注に失敗しました');
    }
  };

  // 注文キャンセル
  const cancelOrder = async (dropshipOrderId: string) => {
    if (!confirm('この注文をキャンセルしますか？')) return;
    try {
      const res = await api.put(`/api/dropship/orders/${dropshipOrderId}/status`, { status: 'cancelled' });
      const data = await res.json();
      if (data.success) {
        await fetchDropshipOrders();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('キャンセルに失敗しました');
    }
  };

  // ステータス変更
  const changeStatus = async (dropshipOrderId: string, newStatus: string) => {
    try {
      const res = await api.put(`/api/dropship/orders/${dropshipOrderId}/status`, { status: newStatus });
      const data = await res.json();
      if (data.success) {
        await fetchDropshipOrders();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('ステータス変更に失敗しました');
    }
  };

  // 転送注文削除（再転送可能にする）
  const deleteDropshipOrder = async (dropshipOrderId: string) => {
    if (!confirm('この転送注文を削除しますか？\n削除後、楽天受注から再度転送注文を作成できます。')) return;
    try {
      const res = await api.delete(`/api/dropship/orders/${dropshipOrderId}`);
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        await fetchDropshipOrders();
        await fetchRakutenOrders();
        await fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  // ページネーション計算
  const totalPages = Math.ceil(rakutenOrders.length / itemsPerPage);
  const paginatedOrders = rakutenOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const dropshipTotalPages = Math.ceil(dropshipOrders.length / itemsPerPage);
  const paginatedDropshipOrders = dropshipOrders.slice(
    (dropshipCurrentPage - 1) * itemsPerPage,
    dropshipCurrentPage * itemsPerPage
  );

  // 全選択/解除
  const toggleSelectAll = () => {
    if (selectedOrderIds.size === rakutenOrders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(rakutenOrders.map(o => o.id)));
    }
  };

  // 個別選択
  const toggleSelectOrder = (orderId: string) => {
    const newSet = new Set(selectedOrderIds);
    if (newSet.has(orderId)) {
      newSet.delete(orderId);
    } else {
      newSet.add(orderId);
    }
    setSelectedOrderIds(newSet);
  };

  // 一括転送注文作成
  const bulkCreateDropshipOrders = async () => {
    const eligibleOrders = rakutenOrders.filter(o => 
      selectedOrderIds.has(o.id) && 
      (!o.dropshipStatus || o.dropshipStatus === 'cancelled' || o.dropshipStatus === 'failed')
    );
    
    if (eligibleOrders.length === 0) {
      setError('転送可能な注文が選択されていません');
      return;
    }

    if (!confirm(`${eligibleOrders.length}件の注文を一括で転送注文に登録しますか？`)) return;
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const order of eligibleOrders) {
      try {
        const res = await api.post('/api/dropship/orders/create', { rakutenOrderId: order.id });
        const data = await res.json();
        if (data.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }

    setLoading(false);
    alert(`一括登録完了\n成功: ${successCount}件\n失敗: ${errorCount}件`);
    setSelectedOrderIds(new Set());
    await fetchRakutenOrders();
    await fetchDropshipOrders();
    await fetchStats();
  };

  // 期間指定で受注同期
  const syncRakutenOrdersWithDays = async () => {
    if (!selectedShopId) {
      setError('ショップを選択してください');
      return;
    }
    setSyncing(true);
    setError(null);
    try {
      // 日付指定があれば使用、なければ日数指定
      let since: string;
      let until: string | undefined;
      
      if (dateFrom) {
        since = new Date(dateFrom).toISOString();
        if (dateTo) {
          until = new Date(dateTo + 'T23:59:59').toISOString();
        }
      } else {
        since = new Date(Date.now() - syncDays * 24 * 60 * 60 * 1000).toISOString();
      }
      
      const res = await api.post('/api/dropship/fetch-rakuten-orders', { 
        shopId: selectedShopId,
        since,
        until
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        await fetchRakutenOrders();
      } else {
        setError(data.error || '受注取得に失敗しました');
      }
    } catch (err) {
      setError('楽天受注同期に失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  // 商品マッピング取得
  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/dropship/mappings');
      const data = await res.json();
      setMappings(data.mappings || []);
    } catch (err) {
      setError('商品マッピングの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 商品マッピング登録
  const createMapping = async () => {
    if (!newMapping.rakutenShopId || !newMapping.amazonShopId || !newMapping.rakutenItemCode || !newMapping.amazonAsin) {
      setError('必須項目を入力してください');
      return;
    }
    try {
      const res = await api.post('/api/dropship/mappings', newMapping);
      const data = await res.json();
      if (data.success) {
        setShowMappingModal(false);
        setNewMapping({
          rakutenShopId: '',
          amazonShopId: '',
          rakutenItemCode: '',
          rakutenItemName: '',
          amazonAsin: '',
          amazonSku: '',
          janCode: '',
        });
        await fetchMappings();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('商品マッピングの登録に失敗しました');
    }
  };

  // 商品マッピング削除
  const deleteMapping = async (id: string) => {
    if (!confirm('このマッピングを削除しますか？')) return;
    try {
      await api.delete(`/api/dropship/mappings/${id}`);
      await fetchMappings();
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  // 初期ロード
  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchRakutenOrders();
    } else if (activeTab === 'dropship') {
      fetchDropshipOrders();
      fetchStats();
    } else if (activeTab === 'mappings') {
      fetchMappings();
    }
  }, [activeTab, fetchRakutenOrders, fetchDropshipOrders, fetchStats, fetchMappings]);

  const renderStatusBadge = (status: string) => {
    const statusInfo = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: null };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.icon}
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">楽天→Amazonドロップシッピング</h1>
          <p className="text-sm text-gray-500 mt-1">
            楽天の受注をAmazonで購入し、直接お客様に発送します
          </p>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle size={20} />
          </button>
        </div>
      )}

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <ShoppingCart size={18} />
              楽天受注一覧
            </span>
          </button>
          <button
            onClick={() => setActiveTab('dropship')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'dropship'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <Truck size={18} />
              転送処理
              {(stats.pending || 0) + (stats.stock_available || 0) > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {(stats.pending || 0) + (stats.stock_available || 0)}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('mappings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'mappings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <Link size={18} />
              商品マッピング
            </span>
          </button>
        </nav>
      </div>

      {/* 楽天受注一覧タブ */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {/* フィルター・アクション */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={selectedShopId}
                onChange={(e) => setSelectedShopId(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">全ての楽天ショップ</option>
                {rakutenShops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.shopName}
                  </option>
                ))}
              </select>
              
              {/* 期間指定 */}
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-gray-500" />
                <select
                  value={syncDays}
                  onChange={(e) => {
                    setSyncDays(Number(e.target.value));
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="border rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  disabled={!!dateFrom}
                >
                  <option value={7}>7日間</option>
                  <option value={14}>14日間</option>
                  <option value={30}>30日間</option>
                  <option value={60}>60日間</option>
                  <option value={90}>90日間</option>
                </select>
                <span className="text-gray-400 text-sm">または</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="開始日"
                />
                <span className="text-gray-500">〜</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="終了日"
                />
                {dateFrom && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    クリア
                  </button>
                )}
              </div>

              <button
                onClick={testRakutenConnection}
                disabled={syncing || !selectedShopId}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                接続テスト
              </button>
              <button
                onClick={syncRakutenOrdersWithDays}
                disabled={syncing || !selectedShopId}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
                楽天から受注取得
              </button>
            </div>
            <button
              onClick={fetchRakutenOrders}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              <RefreshCw size={16} />
              更新
            </button>
          </div>

          {/* 一括操作バー */}
          {selectedOrderIds.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-blue-700">
                <CheckSquare size={16} className="inline mr-1" />
                {selectedOrderIds.size}件選択中
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={bulkCreateDropshipOrders}
                  disabled={loading}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus size={14} />
                  一括転送注文作成
                </button>
                <button
                  onClick={() => setSelectedOrderIds(new Set())}
                  className="flex items-center gap-1 px-3 py-1 text-gray-600 hover:bg-gray-100 text-sm rounded"
                >
                  選択解除
                </button>
              </div>
            </div>
          )}

          {/* 受注一覧 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedOrderIds.size === rakutenOrders.length && rakutenOrders.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">注文番号</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">注文日</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">購入者</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">金額</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">転送状態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : rakutenOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      楽天受注がありません
                    </td>
                  </tr>
                ) : (
                  paginatedOrders.map((order) => (
                    <tr key={order.id} className={`hover:bg-gray-50 ${selectedOrderIds.has(order.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.has(order.id)}
                          onChange={() => toggleSelectOrder(order.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm">{order.mallOrderId}</div>
                        <div className="text-xs text-gray-500">{order.shop?.shopName}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(order.orderedAt).toLocaleString('ja-JP')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium">{order.customerInfo?.name}</div>
                        <div className="text-xs text-gray-500">{order.shippingAddress?.address?.substring(0, 20)}...</div>
                      </td>
                      <td className="px-4 py-3">
                        {order.items.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="text-sm">
                            {item.productName.substring(0, 20)}... x{item.quantity}
                          </div>
                        ))}
                        {order.items.length > 2 && (
                          <div className="text-xs text-gray-500">他{order.items.length - 2}件</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        ¥{Number(order.totalAmount).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {order.dropshipStatus ? (
                          renderStatusBadge(order.dropshipStatus)
                        ) : (
                          <span className="text-xs text-gray-400">未処理</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!order.dropshipStatus || order.dropshipStatus === 'cancelled' || order.dropshipStatus === 'failed' ? (
                          <button
                            onClick={() => createDropshipOrder(order.id)}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            <Plus size={14} />
                            {order.dropshipStatus === 'cancelled' ? '再転送' : order.dropshipStatus === 'failed' ? '再実行' : '転送注文作成'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500">
                            {order.amazonOrderId && `AMZ: ${order.amazonOrderId}`}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* ページネーション */}
          {rakutenOrders.length > 0 && (
            <div className="bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  全 <span className="font-semibold">{rakutenOrders.length}</span> 件中{' '}
                  <span className="font-semibold">{(currentPage - 1) * itemsPerPage + 1}</span>
                  {' '}-{' '}
                  <span className="font-semibold">{Math.min(currentPage * itemsPerPage, rakutenOrders.length)}</span>
                  {' '}件を表示
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={20}>20件</option>
                  <option value={50}>50件</option>
                  <option value={100}>100件</option>
                  <option value={200}>200件</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ‹ 前へ
                </button>
                <span className="text-sm text-gray-700">
                  {currentPage} / {totalPages} ページ
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  次へ ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 転送処理タブ */}
      {activeTab === 'dropship' && (
        <div className="space-y-4">
          {/* ステータスカード */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">処理待ち</div>
              <div className="text-2xl font-bold text-gray-900">{stats.pending || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">在庫あり</div>
              <div className="text-2xl font-bold text-green-600">{stats.stock_available || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">発注完了</div>
              <div className="text-2xl font-bold text-indigo-600">{stats.ordered || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">発送完了</div>
              <div className="text-2xl font-bold text-green-600">{stats.shipped || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">失敗/在庫なし</div>
              <div className="text-2xl font-bold text-red-600">
                {(stats.failed || 0) + (stats.stock_unavailable || 0)}
              </div>
            </div>
          </div>

          {/* 注文一覧 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-medium">転送注文一覧</h3>
              <button
                onClick={fetchDropshipOrders}
                className="flex items-center gap-2 px-3 py-1 border rounded hover:bg-gray-50 text-sm"
              >
                <RefreshCw size={14} />
                更新
              </button>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">楽天注文番号</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">配送先</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amazon注文</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : dropshipOrders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      転送注文がありません
                    </td>
                  </tr>
                ) : (
                  paginatedDropshipOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm">{order.rakutenMallOrderId}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString('ja-JP')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium">{order.shippingName}</div>
                        <div className="text-xs text-gray-500">
                          〒{order.shippingPostalCode}<br />
                          {order.shippingAddress.substring(0, 25)}...
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {order.items.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="text-sm flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${
                              item.stockStatus === 'available' ? 'bg-green-500' :
                              item.stockStatus === 'unavailable' ? 'bg-red-500' :
                              item.stockStatus === 'unmapped' ? 'bg-yellow-500' :
                              'bg-gray-300'
                            }`} />
                            {item.rakutenItemName.substring(0, 20)}... x{item.quantity}
                          </div>
                        ))}
                      </td>
                      <td className="px-4 py-3">
                        {renderStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3">
                        {order.amazonOrderId ? (
                          <div>
                            <div className="text-sm font-medium text-blue-600">{order.amazonOrderId}</div>
                            {order.amazonOrderTotal && (
                              <div className="text-xs text-gray-500">
                                ¥{Number(order.amazonOrderTotal).toLocaleString()}
                              </div>
                            )}
                            {order.amazonTrackingNumber && (
                              <div className="text-xs text-green-600">
                                追跡: {order.amazonTrackingNumber}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-2">
                          {/* アクションボタン */}
                          <div className="flex items-center gap-1 flex-wrap">
                            {order.status === 'pending' && (
                              <button
                                onClick={() => checkAmazonStock(order.id)}
                                className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                              >
                                <Search size={12} />
                                在庫確認
                              </button>
                            )}
                            {order.status === 'stock_available' && (
                              <button
                                onClick={() => executeOrder(order.id)}
                                className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                <Play size={12} />
                                発注実行
                              </button>
                            )}
                            <button
                              onClick={() => setSelectedDropshipOrder(order)}
                              className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-100 text-xs rounded"
                            >
                              <Eye size={12} />
                              詳細
                            </button>
                          </div>
                          {/* ステータス変更・削除 */}
                          <div className="flex items-center gap-1">
                            <select
                              value={order.status}
                              onChange={(e) => changeStatus(order.id, e.target.value)}
                              className="text-xs border rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="pending">処理待ち</option>
                              <option value="checking_stock">在庫確認中</option>
                              <option value="stock_available">在庫あり</option>
                              <option value="stock_unavailable">在庫なし</option>
                              <option value="ordering">発注中</option>
                              <option value="ordered">発注完了</option>
                              <option value="shipping">発送中</option>
                              <option value="shipped">発送完了</option>
                              <option value="delivered">配達完了</option>
                              <option value="cancelled">キャンセル</option>
                              <option value="failed">失敗</option>
                            </select>
                            <button
                              onClick={() => deleteDropshipOrder(order.id)}
                              className="flex items-center gap-1 px-1 py-0.5 text-red-500 hover:bg-red-50 text-xs rounded"
                              title="削除して再転送可能にする"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* 転送処理ページネーション */}
          {dropshipOrders.length > 0 && (
            <div className="bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  全 <span className="font-semibold">{dropshipOrders.length}</span> 件中{' '}
                  <span className="font-semibold">{(dropshipCurrentPage - 1) * itemsPerPage + 1}</span>
                  {' '}-{' '}
                  <span className="font-semibold">{Math.min(dropshipCurrentPage * itemsPerPage, dropshipOrders.length)}</span>
                  {' '}件を表示
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDropshipCurrentPage(1)}
                  disabled={dropshipCurrentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  «
                </button>
                <button
                  onClick={() => setDropshipCurrentPage(p => Math.max(1, p - 1))}
                  disabled={dropshipCurrentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ‹ 前へ
                </button>
                <span className="text-sm text-gray-700">
                  {dropshipCurrentPage} / {dropshipTotalPages} ページ
                </span>
                <button
                  onClick={() => setDropshipCurrentPage(p => Math.min(dropshipTotalPages, p + 1))}
                  disabled={dropshipCurrentPage === dropshipTotalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  次へ ›
                </button>
                <button
                  onClick={() => setDropshipCurrentPage(dropshipTotalPages)}
                  disabled={dropshipCurrentPage === dropshipTotalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 商品マッピングタブ */}
      {activeTab === 'mappings' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              楽天商品とAmazon商品の対応関係を登録します。JAN、ASIN、SKUで紐づけることで自動発注が可能になります。
            </p>
            <button
              onClick={() => setShowMappingModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={16} />
              マッピング追加
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">楽天商品コード</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">楽天商品名</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amazon ASIN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amazon SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">JAN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : mappings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      商品マッピングがありません
                    </td>
                  </tr>
                ) : (
                  mappings.map((mapping) => (
                    <tr key={mapping.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{mapping.rakutenItemCode}</td>
                      <td className="px-4 py-3 text-sm">{mapping.rakutenItemName || '-'}</td>
                      <td className="px-4 py-3">
                        <a
                          href={`https://www.amazon.co.jp/dp/${mapping.amazonAsin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {mapping.amazonAsin}
                          <ExternalLink size={12} />
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm">{mapping.amazonSku || '-'}</td>
                      <td className="px-4 py-3 text-sm">{mapping.janCode || '-'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => deleteMapping(mapping.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* マッピング追加モーダル */}
      {showMappingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold mb-4">商品マッピング追加</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">楽天ショップ *</label>
                  <select
                    value={newMapping.rakutenShopId}
                    onChange={(e) => setNewMapping({ ...newMapping, rakutenShopId: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">選択してください</option>
                    {rakutenShops.map((shop) => (
                      <option key={shop.id} value={shop.id}>{shop.shopName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amazonショップ *</label>
                  <select
                    value={newMapping.amazonShopId}
                    onChange={(e) => setNewMapping({ ...newMapping, amazonShopId: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">選択してください</option>
                    {amazonShops.map((shop) => (
                      <option key={shop.id} value={shop.id}>{shop.shopName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">楽天商品コード *</label>
                <input
                  type="text"
                  value={newMapping.rakutenItemCode}
                  onChange={(e) => setNewMapping({ ...newMapping, rakutenItemCode: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="例: item-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">楽天商品名</label>
                <input
                  type="text"
                  value={newMapping.rakutenItemName}
                  onChange={(e) => setNewMapping({ ...newMapping, rakutenItemName: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="例: サンプル商品A"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amazon ASIN *</label>
                  <input
                    type="text"
                    value={newMapping.amazonAsin}
                    onChange={(e) => setNewMapping({ ...newMapping, amazonAsin: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="例: B0XXXXXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amazon SKU</label>
                  <input
                    type="text"
                    value={newMapping.amazonSku}
                    onChange={(e) => setNewMapping({ ...newMapping, amazonSku: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="例: SKU-001"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">JANコード</label>
                <input
                  type="text"
                  value={newMapping.janCode}
                  onChange={(e) => setNewMapping({ ...newMapping, janCode: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="例: 4900000000000"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowMappingModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                onClick={createMapping}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                登録
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 詳細モーダル */}
      {selectedDropshipOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">転送注文詳細</h3>
              <button onClick={() => setSelectedDropshipOrder(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* 基本情報 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">楽天注文番号</div>
                  <div className="font-medium">{selectedDropshipOrder.rakutenMallOrderId}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">ステータス</div>
                  {renderStatusBadge(selectedDropshipOrder.status)}
                </div>
                <div>
                  <div className="text-sm text-gray-500">Amazon注文番号</div>
                  <div className="font-medium">{selectedDropshipOrder.amazonOrderId || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Amazon発注金額</div>
                  <div className="font-medium">
                    {selectedDropshipOrder.amazonOrderTotal 
                      ? `¥${Number(selectedDropshipOrder.amazonOrderTotal).toLocaleString()}`
                      : '-'
                    }
                  </div>
                </div>
              </div>

              {/* 配送先 */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="text-sm font-medium text-gray-700 mb-2">配送先情報</div>
                <div className="text-sm">
                  <div className="font-medium">{selectedDropshipOrder.shippingName}</div>
                  <div>〒{selectedDropshipOrder.shippingPostalCode}</div>
                  <div>{selectedDropshipOrder.shippingAddress}</div>
                </div>
              </div>

              {/* 商品一覧 */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">商品一覧</div>
                <table className="min-w-full divide-y divide-gray-200 border rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">商品名</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ASIN</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">数量</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">在庫</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedDropshipOrder.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-3 py-2 text-sm">{item.rakutenItemName}</td>
                        <td className="px-3 py-2 text-sm">{item.amazonAsin || '未マッピング'}</td>
                        <td className="px-3 py-2 text-sm">{item.quantity}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.stockStatus === 'available' ? 'bg-green-100 text-green-700' :
                            item.stockStatus === 'unavailable' ? 'bg-red-100 text-red-700' :
                            item.stockStatus === 'unmapped' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {item.stockStatus === 'available' ? `在庫あり (${item.amazonStock})` :
                             item.stockStatus === 'unavailable' ? '在庫なし' :
                             item.stockStatus === 'unmapped' ? '未マッピング' :
                             '未確認'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 追跡情報 */}
              {selectedDropshipOrder.amazonTrackingNumber && (
                <div className="border rounded-lg p-4 bg-green-50">
                  <div className="text-sm font-medium text-green-700 mb-2">配送追跡</div>
                  <div className="text-sm">
                    <div>追跡番号: {selectedDropshipOrder.amazonTrackingNumber}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedDropshipOrder(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

